import { parseDynamicQuestionUploadedFilesFromAnswerText } from './dynamicQuestionUploadedFiles';

const DYNAMIC_ANSWER_EMPTY = '–';

export type DynamicQuestionOption = {
  id?: string | null;
  optionText?: string | null;
};

export function buildOptionsByQuestionId(
  questions:
    | Array<{
        id?: string | null;
        options?: DynamicQuestionOption[] | null;
      }>
    | null
    | undefined,
): Record<string, DynamicQuestionOption[]> {
  if (!questions?.length) return {};
  const map: Record<string, DynamicQuestionOption[]> = {};
  for (const q of questions) {
    const id = q.id ?? '';
    if (!id) continue;
    const opts = q.options?.filter((o) => o.id != null) ?? [];
    map[id] = opts;
  }
  return map;
}

function isDynamicAnswerObject(value: unknown): value is {
  text?: string;
  selectedOptionId?: string;
  selectedOptionIds?: string[];
  dataGridRows?: unknown[] | null;
} {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    ('text' in value ||
      'selectedOptionId' in value ||
      'selectedOptionIds' in value ||
      'dataGridRows' in value)
  );
}

function resolveOptionLabel(
  optionId: string,
  options?: DynamicQuestionOption[] | null,
): string {
  if (!options?.length) return optionId;
  const opt = options.find((o) => o.id === optionId);
  const label = opt?.optionText?.trim();
  return label !== undefined && label !== '' ? label : optionId;
}

export function formatDynamicQuestionAnswerDisplay(
  value: unknown,
  options?: DynamicQuestionOption[] | null,
): string {
  if (value == null) return DYNAMIC_ANSWER_EMPTY;
  if (typeof value === 'string')
    return value.trim() === '' ? DYNAMIC_ANSWER_EMPTY : value;
  if (typeof value === 'number' || typeof value === 'boolean')
    return String(value);
  if (!isDynamicAnswerObject(value)) return DYNAMIC_ANSWER_EMPTY;
  const text = value.text?.trim();
  if (text !== undefined && text !== '') {
    const uploaded = parseDynamicQuestionUploadedFilesFromAnswerText(text);
    if (uploaded.length > 0) {
      return uploaded.map((f) => f.fileName).join(', ');
    }
    return text;
  }
  const singleId = value.selectedOptionId;
  if (singleId) {
    return resolveOptionLabel(singleId, options);
  }
  const ids = value.selectedOptionIds;
  if (Array.isArray(ids) && ids.length > 0) {
    const labels = ids.map((id) => resolveOptionLabel(id, options));
    return labels.join(', ');
  }
  const rows = value.dataGridRows;
  if (Array.isArray(rows) && rows.length > 0)
    return `${rows.length} row${rows.length === 1 ? '' : 's'}`;
  return DYNAMIC_ANSWER_EMPTY;
}
